import { addTypenameToDocument } from "@apollo/client/utilities";
import { GraphQLError, parse } from "graphql";
import { request } from "graphql-request";

import appConfig from "config/app";
import { ensureValidToken, handleUnauthenticatedError } from "src/graphql/auth-handler";
import { useAuthStore } from "src/stores/auth-store";
import { logger, maskSensitiveData, stringify } from "src/utils";

interface ErrorResponse {
  message: string;
  path: Array<string>;
  extensions: {
    [key: string]: unknown;
  };
}

export interface ServerError {
  response?: {
    errors?: Array<GraphQLError>;
  };
  request?: {
    query?: string;
    variables?: {
      [key: string]: unknown;
    };
  };
}

interface GraphQLRequestError {
  response: {
    errors: Array<ErrorResponse>;
  };
  request: {
    [key: string]: unknown;
  };
}

const LOG_PREFIX = "[FetcherNext]";

/**
 * Process errors and determine how to handle them
 * Returns the retry result if successful, null otherwise
 */
const processErrors = async <TData>(
  errors: Array<ErrorResponse>,
  executeRequest: () => Promise<TData>
): Promise<TData | null> => {
  for (const error of errors) {
    const { message, path, extensions } = error;
    const isUpdatePasswordPath = path && path[0] === "updatePassword";

    // Handle UNAUTHENTICATED errors (except for updatePassword)
    if (extensions?.status === 401 && !isUpdatePasswordPath) {
      // Don't logout immediately - try refresh first
      return handleUnauthenticatedError(executeRequest, LOG_PREFIX);
    }

    // Log non-sensitive errors
    logger.error(
      `[GraphQL error]: Message: ${message}, Path: ${path}, Extensions: ${stringify(extensions)}`
    );
  }

  return null;
};

export const fetcher = <TData, TVariables extends Record<string, unknown>>(
  query: string,
  variables?: TVariables,
  // eslint-disable-next-line no-undef
  options?: RequestInit["headers"]
): (() => Promise<TData>) => {
  return async () => {
    // Ensure token is valid before making the request
    await ensureValidToken(LOG_PREFIX);

    // Adds "__typename" to the query
    const document = addTypenameToDocument(parse(query));

    const executeRequest = async (): Promise<TData> => {
      // Get fresh token in case it was refreshed
      const currentState = useAuthStore.getState();
      const currentToken = currentState.token;

      const requestHeaders = {
        ...options,
        authorization: currentToken ? `Bearer ${currentToken}` : "",
      };

      logger.info(
        `query ${query} start with variables: ${JSON.stringify(maskSensitiveData(variables))}`
      );

      if (appConfig.graphql.urlNext) {
        const data = await request<TData>(
          `${appConfig.graphql.urlNext}/graphql`,
          document,
          variables,
          requestHeaders
        );

        logger.info(`query ${query} end with: ${JSON.stringify(data)}`);

        return data;
      }

      throw new Error("GraphQL URL Next not configured");
    };

    try {
      return await executeRequest();
    } catch (err) {
      const error = err as GraphQLRequestError;

      if (error?.response?.errors) {
        // Try to handle errors (may retry with refreshed token)
        const retryResult = await processErrors(error.response.errors, executeRequest);
        if (retryResult !== null) {
          return retryResult;
        }
      }

      throw error;
    }
  };
};
